import { useMemo } from "react"

import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential, useIsFeatureWritable } from "@l2r-front/l2r-networks"

import { API_MODULE_ROADSIDE } from "../../../../common/constants/appRoad"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"

import { useRoadsideInventories } from "../queries/useRoadsideInventories"

export function useRoadRoadsideInventoriesFeatures(road, typeCode, options) {
    const { filterNonWritableFeatures = true, includeNetworkFeatures = true } = (options || {})

    const { t } = useTranslation(I18N_NAMESPACE)
    const { setError: setMapError } = useMapDispatchContext()
    const isFeatureWritable = useIsFeatureWritable(API_MODULE_ROADSIDE)

    const { data: inventories, isLoading: isLoadingInventories, isFetching: isFetchingInventories } = useRoadsideInventories({
        as_geojson: true,
        linear_location_road: road,
        roadside_inventory_type__ancestor: typeCode,
    }, {
        enabled: !!road && !!typeCode,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideSource.error"),
    })

    const { data: referential, isLoading: isLoadingReferential } = useNetworkReferential({
        as_geojson: true,
        linear_location_road: road,
    }, {
        enabled: !!road,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadsideSource.error"),
    })

    // Need to copy id into properties because of that mapbox issue: 
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if ((isLoadingInventories || isFetchingInventories || !inventories) || (isLoadingReferential || !referential?.features?.length)) {
            return null
        }

        const sourceFeatures = inventories.features?.filter(feature => filterNonWritableFeatures ? isFeatureWritable(feature) : true) || []

        if (includeNetworkFeatures) {
            referential?.features.forEach(feature => {
                sourceFeatures.push(feature)
            })
        }

        const newData = sanitizeData({
            type: "FeatureCollection",
            features: sourceFeatures,
        })
        if (sourceFeatures.length) {
            const newFeatures = []
            if (newData) {
                newData.features.reduce((acc, feature) => {
                    const featureLocked = !isFeatureWritable(feature)
                    if (feature.properties["direction"] === "+" || !feature.properties["direction"]) {
                        acc.push({
                            ...feature,
                            type: "Feature",
                            properties: {
                                ...feature.properties,
                                side: "RIGHT",
                                ...(featureLocked && { locked: true }),
                            },
                        })
                    }
                    if (feature.properties["direction"] === "-" || !feature.properties["direction"]) {
                        const inversedLinearLocation = feature.properties["linearLocation"].map(location => ({
                            ...location,
                            start: location.end,
                            end: location.start,
                        }))
                        acc.push({
                            ...feature,
                            type: "Feature",
                            properties: {
                                ...feature.properties,
                                linearLocation: inversedLinearLocation,
                                side: "LEFT",
                                ...(featureLocked && { locked: true }),

                            },
                            geometry: {
                                ...feature.geometry,
                                coordinates: feature.geometry.type === "LineString"
                                    ? (Array.isArray(feature.geometry.coordinates) ? feature.geometry.coordinates.slice().reverse() : feature.geometry.coordinates)
                                    : (Array.isArray(feature.geometry.coordinates)
                                        ? feature.geometry.coordinates.map(coords => Array.isArray(coords) ? coords.slice().reverse() : coords)
                                        : feature.geometry.coordinates),
                            },
                        })
                    }

                    return acc
                }, newFeatures)
                newData.features = newFeatures
            }
        }
        return newData as GeoJSON.FeatureCollection
    }, [isLoadingInventories, isLoadingReferential, inventories, referential, isFeatureWritable])

    return sanitizedData
}